import Application from '../components/application/application';
import 'react-phone-number-input/style.css';
import React, { useState } from 'react';
import { JobContent } from '../models';
import ReactGA from 'react-ga4';
import { Link } from 'react-router-dom';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FileTextOutlined,
  SendOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { containsHtml } from '../utils/formatPlainJobDescription';

interface Props {
  job: JobContent;
}

const JobCard: React.FC<Props> = ({ job }) => {
  const [isOpen, setIsOpen] = useState(false);

  const trackEvent = (category = '', label = '') => {
    try {
      ReactGA.event({
        category,
        action: 'Clicked on ' + label,
        label,
      });
    } catch {
      // Analytics must never block a job action or navigation.
    }
  };

  const handleDetailsClick = () => {
    const label = job.category + ' : ' + job.subCategory + ' : ' + job.name;
    trackEvent('Learn More', label);
  };

  const handleJobClick = () => {
    const label = job.category + ' : ' + job.subCategory + ' : ' + job.name;
    trackEvent('Apply Job', label);

    if (job.featured) setIsOpen(true);
  };

  const htmlToPlainText = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const firstElement = doc.body.firstElementChild;
    if (
      firstElement &&
      /^H[1-4]$/.test(firstElement.tagName) &&
      /^job description:?$/i.test(firstElement.textContent?.trim() ?? '')
    ) {
      firstElement.remove();
    }

    doc.body
      .querySelectorAll('br, p, h1, h2, h3, h4, li, blockquote')
      .forEach((block) => {
        block.append(' ');
      });

    const text = doc.body.textContent?.replace(/\s+/g, ' ').trim() || '';

    return text.length > 220 ? `${text.slice(0, 220)}...` : text;
  };

  const description = containsHtml(job.description)
    ? htmlToPlainText(job.description)
    : job.description.length > 220
      ? `${job.description.slice(0, 220)}...`
      : job.description;

  const companyInitial = (job.company || job.name || 'J')
    .trim()
    .charAt(0)
    .toUpperCase();
  const visibleSkills = job.skills?.slice(0, 4) ?? [];
  const remainingSkills = Math.max(
    (job.skills?.length ?? 0) - visibleSkills.length,
    0
  );
  const locationLabel =
    [job.location, job?.countries?.join(' / ')].filter(Boolean).join(' - ') ||
    'Location flexible';
  const isApplyDisabled =
    job.closed || !job.published || (!job.featured && !job.shortURL.longUrl);

  return (
    <article
      className={`job-opportunity-card${job.remote ? ' is-remote' : ''}`}
    >
      <header className="job-card-top">
        <div className="job-card-identity">
          <div
            className="job-company-mark"
            aria-hidden="true"
            title={job.company || 'Company'}
          >
            {companyInitial}
          </div>
          <span className="job-card-company" title={job.company || 'Company'}>
            {job.company || 'Company'}
          </span>
        </div>
        <span className="job-posted-date">
          <CalendarOutlined />
          {job.postedDate}
        </span>
      </header>

      {(job.featured || job.closed || job.remote) && (
        <div className="job-card-flags">
          {job.featured && (
            <span className="job-featured-pill">
              <FileTextOutlined />
              Easy apply
            </span>
          )}
          {job.remote && <span className="job-remote-pill">Remote</span>}
          {job.closed && <span className="job-closed-pill">Closed</span>}
        </div>
      )}

      <h2 className="job-card-title">{job.name}</h2>

      <div className="job-card-body">
        <ul className="job-meta-list">
          <li>
            <EnvironmentOutlined className="job-meta-icon" />
            <span className="truncate">{locationLabel}</span>
          </li>
          {(job.category || job.subCategory) && (
            <li>
              <TagsOutlined className="job-meta-icon" />
              <span className="truncate">
                {[job.category, job.subCategory].filter(Boolean).join(' / ')}
              </span>
            </li>
          )}
        </ul>

        {description && <p className="job-summary">{description}</p>}

        {visibleSkills.length > 0 && (
          <div className="job-skills-row">
            {visibleSkills.map((skill) => (
              <span key={skill.id || skill.skillName}>{skill.skillName}</span>
            ))}
            {remainingSkills > 0 && <span>+{remainingSkills}</span>}
          </div>
        )}
      </div>

      <div className="job-card-actions">
        {!job.featured && !isApplyDisabled ? (
          <a
            href={job.shortURL.longUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="job-primary-action"
            onClick={handleJobClick}
          >
            <SendOutlined />
            Apply
          </a>
        ) : (
          <button
            type="button"
            className={`job-primary-action ${job.featured ? 'is-featured' : ''} ${
              isApplyDisabled ? 'cursor-not-allowed opacity-60' : ''
            }`}
            onClick={handleJobClick}
            disabled={isApplyDisabled}
          >
            <SendOutlined />
            {job.featured ? 'Easy Apply' : 'Apply'}
          </button>
        )}

        <Link
          to={`/jobs/${job.id}`}
          className="job-secondary-action"
          onClick={handleDetailsClick}
        >
          <EyeOutlined />
          Details
        </Link>
      </div>

      <Application isOpen={isOpen} job={job} onClose={() => setIsOpen(false)} />
    </article>
  );
};

export default JobCard;
